/**
 * Pure step-builders for the cross-board "Your Tiles" manage view.
 *
 * The manage modal lists every seat the holder owns across all V2
 * boards in one flat list. A bulk action (reprice / top-up / withdraw /
 * abandon) on a multi-board selection can't be a single transaction —
 * `setPrice`/`withdrawDeposit`/`abandon` on the UGM are all bound to
 * `msg.sender == seat.holder`, so the user's own wallet fans the work
 * out into **one transaction per board/grid** (the transaction queue
 * sequences them; an EIP-5792 wallet may batch several). These helpers
 * turn a resolved selection into that ordered set of calls.
 *
 * Kept free of React / network so the grouping + USD→token conversion +
 * skip-if-unchanged logic is unit-testable in isolation. Live allowance
 * reads stay in the component: `buildManageSteps` reports the
 * per-(token, manager) approval *requirements*, and the caller prepends
 * approve steps after reading each spender's current allowance.
 */

import { encodeFunctionData, type Hex } from 'viem';
import {
  buildSetPriceCall,
  buildAddDepositCalls,
  buildWithdrawDepositCall,
} from '@takeover/sdk/contracts';
import { abiCallToTx } from '@/lib/sdk-tx';
import { UGM_ABI } from '@/lib/contracts';
import { parseTokenPrice, type TaxTokenConfig } from '@/lib/drawer-utils';

export type DepositMode = 'top-up' | 'withdraw';

export interface ManageTxCall {
  to: Hex;
  data: Hex;
}

export interface ManageStep {
  id: string;
  label: string;
  description?: string;
  call: ManageTxCall;
}

/** One selected seat, resolved to everything needed to target it onchain. */
export interface ManageSelectionRow {
  /** Stable row id, `${poolId}-${seatIndex}`. */
  id: string;
  managerAddress: Hex;
  gridId: bigint;
  seatIndex: number;
  /** Tax-token config for this board (decimals / symbol / isTakeover / tokenAddress). */
  ttc: TaxTokenConfig;
  /** Current onchain self-assessed price (raw token units). */
  rawPrice: bigint;
  /** Current onchain deposit balance (raw token units). */
  rawBalance: bigint;
  /** Board ticker, for step labels. */
  boardSymbol: string;
  /** 1-indexed display label (e.g. "042"), for step labels. */
  seatLabel: string;
}

export interface ManageInputs {
  /** New self-assessed price in USD, or null to leave price unchanged. */
  priceUsd: number | null;
  /** Deposit change in USD (per seat), or null/0 for no change. */
  depositUsd: number | null;
  depositMode: DepositMode;
  /** TAKEOVER/USD oracle price for converting USD → TAKEOVER raw. */
  takeoverPriceUsd: number;
}

export interface ApprovalNeed {
  /** ERC-20 tax token to approve. */
  token: Hex;
  /** Spender = the UGM manager that will pull the deposit. */
  spender: Hex;
  /** Total raw amount to be pulled across all top-ups for this (token, spender). */
  total: bigint;
}

export interface BuiltManageSteps {
  /** Reprice + deposit/withdraw steps (no approvals). */
  steps: ManageStep[];
  /** Per-(token, spender) approval requirements for top-ups. The caller
   *  reads live allowance and prepends approve steps for any shortfall. */
  approvals: ApprovalNeed[];
  repriceCount: number;
  depositCount: number;
  withdrawCount: number;
  /** Distinct boards (manager + grid) touched by the selection. */
  boardCount: number;
}

/**
 * Convert a USD amount to raw tax-token units for a given board.
 * USDC (6dp) is 1:1; TAKEOVER is divided by the live oracle price and
 * clamped to the token's decimals via `parseTokenPrice`. Mirrors the
 * conversion the single-board manage view has always used so display
 * and calldata stay consistent.
 */
export function usdToTokenRaw(
  usd: number,
  ttc: TaxTokenConfig,
  takeoverPriceUsd: number,
): bigint {
  if (!Number.isFinite(usd) || usd <= 0) return 0n;
  if (ttc.isTakeover && takeoverPriceUsd > 0) {
    const tokenAmount = usd / takeoverPriceUsd;
    return parseTokenPrice(
      tokenAmount.toFixed(Math.min(ttc.decimals, 18)),
      ttc.decimals,
    );
  }
  return parseTokenPrice(usd.toString(), ttc.decimals);
}

function boardKey(r: { managerAddress: Hex; gridId: bigint }): string {
  return `${r.managerAddress.toLowerCase()}-${r.gridId.toString()}`;
}

/**
 * Build the reprice + deposit/withdraw steps for a cross-board
 * selection, plus the approval requirements for any top-ups.
 *
 * Per-board nuances handled here:
 *   - Price/deposit USD inputs are converted to each board's tax token
 *     via {@link usdToTokenRaw}, so a mixed USDC + TAKEOVER selection
 *     gets the right raw amount per row.
 *   - Reprice skips seats already at the target price (no-op tx).
 *   - Withdraw caps per-seat at the current balance so one under-funded
 *     seat can't revert the rest of the queue.
 *   - Top-up approvals are summed per (tax token, manager) so the caller
 *     issues one approval covering every seat on that manager.
 */
export function buildManageSteps(
  rows: ManageSelectionRow[],
  inputs: ManageInputs,
): BuiltManageSteps {
  const { priceUsd, depositUsd, depositMode, takeoverPriceUsd } = inputs;
  const hasPrice =
    priceUsd != null && Number.isFinite(priceUsd) && priceUsd >= 0;
  const hasDeposit =
    depositUsd != null && Number.isFinite(depositUsd) && depositUsd > 0;

  const steps: ManageStep[] = [];
  const approvalsByKey = new Map<string, ApprovalNeed>();
  const boards = new Set<string>();
  let repriceCount = 0;
  let depositCount = 0;
  let withdrawCount = 0;

  for (const r of rows) boards.add(boardKey(r));

  // 1. Reprice — skip seats whose onchain price already matches target.
  if (hasPrice) {
    for (const r of rows) {
      const newPrice = usdToTokenRaw(priceUsd, r.ttc, takeoverPriceUsd);
      if (newPrice <= 0n || r.rawPrice === newPrice) continue;
      steps.push({
        id: `reprice-${r.id}`,
        label: `Reprice ${r.boardSymbol} #${r.seatLabel}`,
        call: abiCallToTx(
          buildSetPriceCall({
            gridManagerAddress: r.managerAddress,
            gridId: r.gridId,
            seatIndex: r.seatIndex,
            newPrice,
          }),
        ),
      });
      repriceCount += 1;
    }
  }

  // 2. Deposit / withdraw.
  if (hasDeposit) {
    for (const r of rows) {
      const amount = usdToTokenRaw(depositUsd, r.ttc, takeoverPriceUsd);
      if (amount <= 0n) continue;

      if (depositMode === 'top-up') {
        const token = r.ttc.tokenAddress as Hex;
        const key = `${token.toLowerCase()}-${r.managerAddress.toLowerCase()}`;
        const need = approvalsByKey.get(key);
        if (need) need.total += amount;
        else
          approvalsByKey.set(key, {
            token,
            spender: r.managerAddress,
            total: amount,
          });
        steps.push({
          id: `deposit-${r.id}`,
          label: `Top up ${r.boardSymbol} #${r.seatLabel}`,
          call: abiCallToTx(
            buildAddDepositCalls({
              gridManagerAddress: r.managerAddress,
              gridId: r.gridId,
              seatIndex: r.seatIndex,
              amount,
              usdcAddress: token,
            }).depositCall,
          ),
        });
        depositCount += 1;
      } else {
        const amt = r.rawBalance < amount ? r.rawBalance : amount;
        if (amt <= 0n) continue;
        steps.push({
          id: `withdraw-${r.id}`,
          label: `Withdraw ${r.boardSymbol} #${r.seatLabel}`,
          call: abiCallToTx(
            buildWithdrawDepositCall({
              gridManagerAddress: r.managerAddress,
              gridId: r.gridId,
              seatIndex: r.seatIndex,
              amount: amt,
            }),
          ),
        });
        withdrawCount += 1;
      }
    }
  }

  return {
    steps,
    approvals: [...approvalsByKey.values()],
    repriceCount,
    depositCount,
    withdrawCount,
    boardCount: boards.size,
  };
}

/**
 * One `abandonBatch(gridId, seatIds)` step per board/grid. The UGM's
 * `abandonBatch` is single-grid, so a cross-board abandon fans out into
 * one call per manager+grid; refunds land on the holder's wallet.
 */
export function buildAbandonSteps(rows: ManageSelectionRow[]): ManageStep[] {
  const byBoard = new Map<
    string,
    { managerAddress: Hex; gridId: bigint; boardSymbol: string; seatIds: bigint[] }
  >();
  for (const r of rows) {
    const key = boardKey(r);
    let g = byBoard.get(key);
    if (!g) {
      g = {
        managerAddress: r.managerAddress,
        gridId: r.gridId,
        boardSymbol: r.boardSymbol,
        seatIds: [],
      };
      byBoard.set(key, g);
    }
    g.seatIds.push(BigInt(r.seatIndex));
  }

  const steps: ManageStep[] = [];
  for (const g of byBoard.values()) {
    steps.push({
      id: `abandon-${g.managerAddress.toLowerCase()}-${g.gridId.toString()}`,
      label: `Abandon ${g.seatIds.length} tile${g.seatIds.length === 1 ? '' : 's'} on ${g.boardSymbol}`,
      description: 'Return deposit balance to wallet',
      call: {
        to: g.managerAddress,
        data: encodeFunctionData({
          abi: UGM_ABI,
          functionName: 'abandonBatch',
          args: [g.gridId, g.seatIds],
        }),
      },
    });
  }
  return steps;
}
